import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";

// Global Settings: permanently delete test payments made before a date (preview → type DELETE).
export const TestPaymentCleanup = () => {
  const [date, setDate] = useState("2026-10-20");
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get("admin/cleanup/payments-before", { params: { date } });
      setPreview(data);
      setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not count payments");
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post("admin/cleanup/payments-before", { date, confirm });
      toast.success(`${data.total} records made before ${date} were permanently deleted.`);
      setPreview(null);
      setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const live = preview?.items.reduce((s, i) => s + i.live_stripe, 0) || 0;

  return (
    <section className="space-y-3 pt-4 border-t border-red-200" data-testid="test-payment-cleanup">
      <h4 className="text-sm font-semibold text-red-800 flex items-center gap-1.5">
        <Trash2 className="h-4 w-4" /> Delete test payments
      </h4>
      <p className="text-xs text-slate-500">
        Permanently deletes membership payments, renewals, event bookings, season pass purchases and manual income made before the date.
        This cannot be undone. Members' accounts are kept.
      </p>
      <div className="flex items-center gap-2">
        <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setPreview(null); }} className="w-44 h-8" data-testid="cleanup-date" />
        <Button size="sm" variant="outline" className="h-8" onClick={load} disabled={busy || !date} data-testid="cleanup-preview">
          {busy && !preview ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} Count records
        </Button>
      </div>
      {preview && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2 text-xs" data-testid="cleanup-summary">
          {preview.items.map((i) => (
            <div key={i.collection} className="flex justify-between">
              <span>{i.label}</span>
              <span className="font-semibold">{i.count}{i.live_stripe ? ` (${i.live_stripe} real card payments)` : ""}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-red-200 pt-1 font-semibold">
            <span>Total</span><span data-testid="cleanup-total">{preview.total}</span>
          </div>
          {live > 0 && (
            <p className="flex items-start gap-1 text-red-800 font-medium" data-testid="cleanup-live-warning">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              {live} of these were paid with a real (live) card in Stripe, not in test mode. Check they really are tests before deleting.
            </p>
          )}
          {preview.total > 0 && (
            <div className="flex items-center gap-2 pt-1">
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type DELETE" className="h-8 w-36 bg-white" data-testid="cleanup-confirm-input" />
              <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700 text-white" disabled={busy || confirm !== "DELETE"} onClick={run} data-testid="cleanup-run">
                {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />} Delete {preview.total} records
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
